import { useTranslation } from 'react-i18next';
import { FormControl, IconButton } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

import { CLASSIFICATION_MU } from '@/api/domains/classifications.types';
import { CustomFormLabel, RHFAutocomplete, RHFIntegerTextField } from '@/components/form-controls';
import { RHFInnField } from '@/components/form-controls/RHFInnField';
import { ReactComponent as DeleteIconSVG } from '@/shared/assets/icons-2.0/delete.svg';
import { useClassifications } from '@/shared/hooks/useClassifications';

type ActiveSubstanceItemProps = {
  onRemoveClick: VoidFunction;
  index: number;
  isRequired?: boolean;
  releaseFormId: number;
};

export const ActiveSubstanceItem = (props: ActiveSubstanceItemProps) => {
  const { t } = useTranslation('nomenclature-page');
  const { onRemoveClick, index, isRequired = false, releaseFormId } = props;

  const { isFetching: isFetchingMU, classifications: measureUnits } = useClassifications({
    alias: CLASSIFICATION_MU,
    release_form_id: releaseFormId,
  });

  return (
    <Grid direction="row" columnSpacing={2} rowSpacing={6} container alignItems="flex-end" xs={12} marginRight={-2}>
      <Grid xs={6}>
        <FormControl fullWidth>
          <CustomFormLabel isRequired={isRequired} htmlFor="activeSubstance">
            {t('modals.form.ui.expert-data.active-substance')}
          </CustomFormLabel>
          <RHFInnField
            isSingleComponentInns
            fullWidth
            name={`activeSubstances.${index}.inn`}
            id={`activeSubstances.${index}.inn`}
            TextFieldProps={{ variant: 'filled' }}
          />
        </FormControl>
      </Grid>

      <Grid xs={2}>
        <FormControl fullWidth>
          <CustomFormLabel isRequired={isRequired} htmlFor="dosage">
            {t('modals.form.ui.expert-data.dosage')}
          </CustomFormLabel>
          <RHFIntegerTextField
            variant="filled"
            name={`activeSubstances.${index}.dosage`}
            decimalScale={15}
            decimalSeparator=","
          />
        </FormControl>
      </Grid>

      <Grid xs={2}>
        <FormControl fullWidth>
          <CustomFormLabel isRequired={isRequired} htmlFor="measureUnit">
            {t('modals.form.ui.expert-data.measure-unit')}
          </CustomFormLabel>
          <RHFAutocomplete
            fullWidth
            name={`activeSubstances.${index}.measure_unit`}
            id={`activeSubstances.${index}.measure_unit`}
            loading={isFetchingMU}
            loadingText={t('modals.form.ui.loading')}
            noOptionsText={t('modals.form.ui.no-data')}
            options={measureUnits}
            getOptionLabel={option => (typeof option === 'string' ? option : `${option.name}`)}
            renderOption={(props, option) => <li {...props}>{`${option.name} - ${option.description}`}</li>}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            TextFieldProps={{ variant: 'filled', placeholder: t('modals.form.ui.search') }}
            slotProps={{ popper: { style: { width: 'fit-content' } } }}
            disableClearable
          />
        </FormControl>
      </Grid>

      <Grid xs="auto" paddingRight={0}>
        <IconButton onClick={onRemoveClick}>
          <DeleteIconSVG />
        </IconButton>
      </Grid>
    </Grid>
  );
};
